import React from 'react';
import styled, { useTheme } from 'styled-components';
import { Mono } from '../../../../../generic/ui/Mono';

interface Props {
    percent: number;
    size?: number;
    color?: string;
    className?: string;
}

const STROKE_WIDTH = 4;

const FillGauge: React.FC<Props> = ({ percent, size = 44, color, className }) => {
    const theme = useTheme();
    const clamped = Math.max(0, Math.min(100, percent || 0));
    const radius = (size - STROKE_WIDTH) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference * (1 - clamped / 100);

    return (
        <Wrap className={className} $size={size} role='img' aria-label={`${Math.floor(percent || 0)}%`}>
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill='none'
                    stroke={theme.colors.line2}
                    strokeWidth={STROKE_WIDTH}
                />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill='none'
                    stroke={color ?? theme.colors.accent}
                    strokeWidth={STROKE_WIDTH}
                    strokeLinecap='round'
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    transform={`rotate(-90 ${size / 2} ${size / 2})`}
                />
            </svg>
            <Label>{Math.floor(percent || 0)}%</Label>
        </Wrap>
    );
};

export default FillGauge;

const Wrap = styled.span<{ $size: number }>`
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: ${({ $size }) => $size}px;
    height: ${({ $size }) => $size}px;
    flex-shrink: 0;

    svg {
        display: block;
    }

    svg circle {
        transition: stroke-dashoffset ${({ theme }) => theme.motion.base} ease;
    }
`;

const Label = styled(Mono)`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: ${({ theme }) => theme.text.xs};
    font-weight: 600;
    color: ${({ theme }) => theme.colors.ink};
`;
